import useSWR from "swr";
import axiosInstance, { type ApiResponse, useGet } from "@/api";
import { fetcher } from "@/api/fetcher";
import { isForbiddenError } from "@/api/http-errors";
import { getOrganisationId } from "@/hooks";
import type {
  ICurrentStaffInfoResponse,
  TRoleResponse,
  UserResponse,
} from "@/pages/AdditionalInfo/types";
import roleService from "@/services/role";
import { Logger } from "@/utils";
import { buildStaffManagementUrl } from "@/utils/serviceHelpers";
import { getHospitalClinic } from "../stores/domains/additionalInfo/useAdditionalInfo";

/**
 * Specialty information interface
 */
export interface ISpecialty {
  id: number;
  name: string;
  code: string;
  description: string;
  edenDescription: string;
  nehrDescription: string;
}

export interface ISpecialtyOutpatient {
  id: string;
  specialty: string;
  specialtyId: string | null;
  specialtyCode: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  creatorName: string;
  updatedBy: string;
  updaterName: string;
  version: number;
  deletedAt: string | null;
}

/**
 * Application information interface
 */
export interface IApplication {
  id: number;
  name: string;
  code: string;
}

/**
 * Staff location/organization information interface
 */
export interface IStaffLocation {
  organisationId: string;
  organisationName: string;
  locationId: string;
  locationName: string;
  locationCode: string | null;
  applications: IApplication[];
}

/**
 * Staff status enumeration
 */
export enum EStaffStatus {
  Active = "Active",
  Inactive = "Inactive",
}

/**
 * Permission information interface
 */
export interface IPermission {
  id: number;
  name: string;
  displayName: string;
}

/**
 * Role information interface
 */
export interface IRole {
  id: number;
  name: string;
  permissions: IPermission[];
}

/**
 * Create staff request interface
 */
export interface ICreateStaffRequest {
  name: string;
  email: string;
  startDate: string;
  phoneNumber: string;
  phoneNumberCountryCode: string;
  specialtyId: number;
  roleIds: number[];
  applications: string[];
  note: string;
}

/**
 * Update staff request interface
 */
export interface IUpdateStaffRequest {
  name?: string;
  email?: string;
  startDate?: string;
  expiryDate?: string;
  phoneNumber?: string;
  phoneNumberCountryCode?: string;
  specialtyId?: number;
  roleIds?: number[];
  applications?: string[];
  note?: string;
  isActive?: boolean;
}

/**
 * Create staff response interface
 */
export interface ICreateStaffResponse {
  id: number;
  userId: string;
  username: string;
  fullName: string;
  email: string;
  idNumber: string;
  employeeId: string;
  phoneNumber: string;
  phoneNumberCountryCode: string;
  specialtyId: number;
  specialty: ISpecialty;
  location: IStaffLocation;
  startDate: string;
  note: string;
  createdAt: string;
  createdBy: string;
  lastLogin: string;
  creatorName: string;
  status: EStaffStatus;
  roles: IRole[];
}

/**
 * Current staff information interface
 */
export interface ICurrentStaff {
  id: number;
  userId: string;
  username: string;
  fullName: string;
  email: string;
  idNumber: string;
  employeeId: string;
  phoneNumber: string;
  phoneNumberCountryCode: string;
  specialtyId: number;
  specialty: ISpecialty;
  locations: IStaffLocation[];
  startDate: string;
  note: string;
  createdAt: string;
  createdBy: string;
  lastLogin: string;
  creatorName: string;
  status: EStaffStatus;
}

export interface ITerminalEntity {
  id: number;
  terminalId: string;
  organisationId: string;
  locationId: string;
}

export interface User {
  id: string;
  userId: string;
  username: string;
  fullName?: string;
  createdAt: string;
  email: string;
  name: string;
  phoneNumber?: string;
  phoneNumberCountryCode?: string;
  startDate: string | null;
  expiryDate: string | null;
  lastLogin: Date | null;
  isActive: boolean;
  isAD: boolean;
  roles: IRole[];
  roleGroups: RoleGroup[];
  specialtyId: number | null;
  specialty: ISpecialty | null;
  location: IStaffLocation | null;
  note: string | null;
  registrationNumber?: string | null;
}

export interface RoleGroup {
  id: number;
  name: string;
  roles: (IRole | null)[];
}

export interface Status {
  id: string;
  name: string;
}
export interface ActivityLog {
  id: string;
  activity: string;
  createdAt: string;
  executedBy: string;
}

interface EdenListResponse<T> {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  items: T[];
}

type UserListResponse = ApiResponse<EdenListResponse<User>>;

type StaffActiviteResponse = ApiResponse<EdenListResponse<ActivityLog>>;

interface ParamsGetUsers {
  pageNumber?: number;
  pageSize?: number;
  keyword?: string;
  status: string[];
  createdFromDate: string | number | null;
  createdToDate: string | number | null;
}

/**
 * Request parameters for getting current staff information
 */
export interface IGetCurrentStaffParams {
  /** Whether to include specialty information */
  IncludeSpecialty?: boolean;
  /** Whether to include last login time */
  IncludeLastLogin?: boolean;
  /** Whether to include creator name */
  IncludeCreatorName?: boolean;
  IncludeLocations?: boolean;
}

/**
 * Staff management service
 */
const staffService = {
  /**
   * Get current staff information - Promise approach
   * @param params Request parameters
   * @returns Promise<ApiResponse<ICurrentStaff>>
   */
  getCurrentStaff: (params?: IGetCurrentStaffParams): Promise<ApiResponse<ICurrentStaff>> => {
    const url = "/api/staffs/current";

    // Clean undefined parameters
    const cleanParams = params
      ? Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined))
      : {};

    return axiosInstance.get(url, { params: cleanParams }).then((res) => res.data);
  },

  /**
   * Get current staff information - SWR Hook approach
   * @param params Request parameters
   * @param enabled Whether to enable the request
   * @returns SWR hook result
   */
  useCurrentStaff: (params?: IGetCurrentStaffParams, enabled = true) => {
    const url = "/api/staffs/current";

    // Clean undefined parameters
    const cleanParams = params
      ? Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined))
      : {};

    // Following project's useGet usage pattern, pass params as second parameter
    return useGet<ICurrentStaff>(enabled ? url : null, {
      params: cleanParams,
    });
  },

  /**
   * Create staff - Promise approach
   * @param organisationId Organization ID
   * @param hospitalClinicId Hospital clinic ID
   * @param data Staff creation data
   * @returns Promise<ApiResponse<ICreateStaffResponse>>
   */
  createStaff: (
    organisationId: string,
    hospitalClinicId: string,
    data: ICreateStaffRequest,
  ): Promise<ApiResponse<ICreateStaffResponse>> => {
    const url = `/api/aggregator/organisations/${organisationId}/hospital-clinics/${hospitalClinicId}/staffs`;

    return axiosInstance.post(url, data).then((res) => res.data);
  },

  /**
   * getRoleList - full role list through the MS30 Echo Aggregator.
   *
   * Delegates to `roleService` so the aggregator URL has a single owner, but swallows a 403.
   * User Management is reachable by every role holding `UserListing.View`, while the aggregator
   * gates `GET /roles` on `RoleRoleGroup.View` - a strictly smaller set. The call sites load the
   * role list inside a `Promise.all` alongside locations, specialties and chart-of-accounts, so
   * letting the 403 propagate would empty every other dropdown as well. Role Management keeps
   * calling `roleService.getRoleList` directly and still surfaces the error there.
   *
   * @returns The role list, or an empty page when the caller may not read roles
   */
  getRoleList: async (): Promise<TRoleResponse> => {
    try {
      return await roleService.getRoleList();
    } catch (error) {
      if (!isForbiddenError(error)) throw error;

      Logger.warn("Current role may not list roles; role dropdown will be empty");
      return {
        items: [],
        pageNumber: 1,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }
  },

  getCurrentRoleList: async (): Promise<UserResponse> => {
    const url = `/api/users/current`;
    const response = await axiosInstance.get(url, {});
    return response.data.data;
  },

  /**
   * getUserRole - get user function module by userID
   * @returns Promise<ModuleFunction>
   */
  getUserRole: async (userId: string | number): Promise<User> => {
    const url = `/api/users/${userId}`;
    const response = await axiosInstance.get(url, {});
    return response.data.data;
  },

  /**
   *
   * @param payload - { username: string; password: string }
   * @returns Promise<void> - no return data
   */

  generateResetPasswordApi: async (payload: {
    username: string;
    password: string;
  }): Promise<void> => {
    const url = `/api/users/set-password`;
    await axiosInstance.post(url, payload);
  },

  checkPermission: async (): Promise<IPermission[]> => {
    const url = `/api/aggregator/users/current/permissions`;
    const response = await axiosInstance.get(url, {});
    return response.data.data;
  },

  /**
   * get terminal- Promise approach
   * @param organisationId Organization ID
   * @param hospitalClinicId Hospital clinic ID
   * @returns Promise<Array<ITerminalEntity>>
   */
  getTerminalByIdList: async (
    organisationId: string,
    hospitalClinicId: string,
  ): Promise<Array<ITerminalEntity>> => {
    const url = `/api/terminal-management/v1/organisations/${organisationId}/hospital-clinics/${hospitalClinicId}/terminals`;

    const response = await axiosInstance.get(url, {});

    return response.data.data;
  },

  /**
   * get terminal- Promise approach
   * @returns Promise<Array<ITerminalEntity>>
   */
  getTerminalList: async (
    organisationId: string,
    hospitalClinicId: string,
    terminalId: string,
  ): Promise<Array<ITerminalEntity>> => {
    const url = `/api/terminal-management/v1/terminals`;

    const response = await axiosInstance.post(
      url,
      {
        terminalId,
        organisationId,
        hospitalClinicId,
      },
      {},
    );

    return response.data.data;
  },

  getSpecialtyList: async () => {
    const url = "/api/master-datas/specialties";
    const response = await axiosInstance.get(url);
    return response.data.data;
  },

  getSpecialtyListOutpatient: async (): Promise<ISpecialtyOutpatient[]> => {
    // ECHO-3714: only active NEHR_SS specialties in the referral dropdown (legacy rows are deactivated, kept for history)
    const url = "/api/outpatient/specialty?limit=999&isActive=true";
    const response = await axiosInstance.get(url);
    return response.data.data;
  },

  // Hooks get staffs
  useGetStaffs: (params: ParamsGetUsers) => {
    const url = buildStaffManagementUrl("/staffs/filter");
    return useSWR([url, params] as const, async ([requestUrl, requestParams]) => {
      const response = await axiosInstance.get<UserListResponse>(requestUrl, {
        params: requestParams,
      });
      const userIds = response.data.data.items.map((user) => user.userId);
      const userSearchUrl = `/api/users/search`;

      // /api/users/search caps pageSize at 100, so batch the ids — otherwise staff
      // beyond the first 100 get no roles and are dropped from the merged list (ECHO-3253)
      const USER_SEARCH_BATCH_SIZE = 100;
      const batches: string[][] = [];
      for (let i = 0; i < userIds.length; i += USER_SEARCH_BATCH_SIZE) {
        batches.push(userIds.slice(i, i + USER_SEARCH_BATCH_SIZE));
      }
      const batchResults = await Promise.all(
        batches.map((ids) =>
          axiosInstance.post<UserListResponse>(userSearchUrl, {
            userIds: ids,
            pageNumber: 1,
            pageSize: USER_SEARCH_BATCH_SIZE,
          }),
        ),
      );

      const rolesMap = new Map<
        string,
        {
          roles: IRole[];
          lastLogin: Date | null;
          phoneNumber?: string;
          phoneNumberCountryCode?: string;
          isActive?: boolean;
          name: string;
        }
      >();
      batchResults
        .flatMap((res) => res.data.data.items)
        .forEach((u) => {
          rolesMap.set(u.id, {
            roles: u.roles,
            lastLogin: u.lastLogin,
            phoneNumber: u.phoneNumber,
            phoneNumberCountryCode: u.phoneNumberCountryCode,
            isActive: u.isActive,
            name: u.name,
          });
        });
      const cloneItems: User[] = [];
      // Assign roles for each user
      response.data.data.items.forEach((user) => {
        if (rolesMap.has(user.userId)) {
          cloneItems.push({
            ...user,
            roles: rolesMap.get(user.userId)?.roles ?? [],
            lastLogin: rolesMap.get(user.userId)?.lastLogin ?? null,
            phoneNumber: rolesMap.get(user.userId)?.phoneNumber,
            phoneNumberCountryCode: rolesMap.get(user.userId)?.phoneNumberCountryCode,
            isActive: rolesMap.get(user.userId)?.isActive ?? false,
            name: rolesMap.get(user.userId)?.name ?? "",
          });
        }
      });
      response.data.data.items = cloneItems;
      return response.data;
    });
  },

  getStaffDetail: async (userId: string) => {
    const response = await axiosInstance.get<ApiResponse<User>>(
      buildStaffManagementUrl(`/staffs/${userId}`),
      {
        params: {
          IncludeSpecialty: true,
          IncludeOrganisationName: true,
          IncludeLastLogin: true,
          IncludeCreatorName: true,
        },
      },
    );
    return response.data;
  },

  updateStaffDetail: async (userId: string, data: IUpdateStaffRequest) => {
    const organisationId = getOrganisationId();
    const hospitalClinic = getHospitalClinic();
    const hospitalClinicId = hospitalClinic?.value ?? "";
    const url = `/api/aggregator/organisations/${organisationId}/hospital-clinics/${hospitalClinicId}/staffs/${userId}`;

    const response = await axiosInstance.put<ApiResponse<User>>(url, data);
    return response.data;
  },

  toggleStatusStaff: async (userId: string | number, isActive: boolean) => {
    const url = `/api/users/${userId}/toggle-status`;
    const response = await axiosInstance.put<ApiResponse<User>>(url, { isActive }, {});
    return response.data;
  },

  getStaffActivities: async (userId: string) => {
    const response = await axiosInstance.get<StaffActiviteResponse>(
      `/api/users/${userId}/activities?&PageNumber=1&PageSize=9999`,
      {},
    );
    return response.data;
  },

  getDoctorsByMcr: async (mcr: string) => {
    const url = `/api/doctors/by-mcr/${mcr}`;
    const response = await axiosInstance.get(url);
    return response.data;
  },

  getCOATableData: async () => {
    const url = `/api/master-datas/billing-specialties`;
    const response = await axiosInstance.get(url);
    return response.data;
  },
};

/**
 * Staff management direct API calls (not dependent on hooks)
 */
export const staffDirectApi = {
  /**
   * Get current staff information - Direct API call
   * @param params Request parameters
   * @returns Promise<ICurrentStaffInfoResponse>
   */
  getCurrentStaff: async (params?: IGetCurrentStaffParams): Promise<ICurrentStaffInfoResponse> => {
    try {
      const url = "/api/staff-management/staffs/current";

      // Clean undefined parameters
      const cleanParams = params
        ? Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined))
        : {};

      // Build complete URL with query parameters
      const searchParams = new URLSearchParams();
      Object.entries(cleanParams).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });

      const fullUrl = searchParams.toString() ? `${url}?${searchParams.toString()}` : url;

      return await fetcher<ICurrentStaffInfoResponse>(fullUrl);
    } catch (error) {
      console.error("Error fetching current staff:", error);
      throw error;
    }
  },

  /**
   * Create staff - Direct API call
   * @param organisationId Organization ID
   * @param hospitalClinicId Hospital clinic ID
   * @param data Staff creation data
   * @returns Promise<ICreateStaffResponse>
   */
  createStaff: async (
    organisationId: string,
    hospitalClinicId: string,
    data: ICreateStaffRequest,
  ): Promise<ICreateStaffResponse> => {
    try {
      const url = `/api/aggregator/organisations/${organisationId}/hospital-clinics/${hospitalClinicId}/staffs`;

      const response = await axiosInstance.post<ApiResponse<ICreateStaffResponse>>(url, data);
      return response.data.data;
    } catch (error) {
      console.error("Error creating staff:", error);
      throw error;
    }
  },
};

/**
 * Convenient export functions
 */
export const getCurrentStaff = staffDirectApi.getCurrentStaff;
export const useCurrentStaff = staffService.useCurrentStaff;
export const createStaff = staffDirectApi.createStaff;

export default staffService;
